import { $ } from '../../platform/dom';
/* ============================================================
   畫面 router 與鍵盤焦點導航（2026-08-05）
   ============================================================

   ## 為什麼 router 不持有狀態

   顯示機制仍然是 `.show` class，而且**其他模組會直接動它**
   （`ui/levelup.ts` 的 `elUp.classList.add('show')`、`ui/hangar.ts`、`ui/codex.ts`），
   `sim/input.ts:72` 更是把 `.show` 當成「現在開的是哪一個畫面」的唯一判準。

   所以 router 的做法是**讀 DOM 而不是自己記一份**：哪個畫面開著，永遠以
   `.show` 為準。兩份狀態遲早會岔開，而岔開的症狀（鍵盤打到看不見的畫面上）
   極難重現。`MutationObserver` 只用來在畫面開關時同步 body 的 class 與初始焦點。

   ## 焦點導航

   `03-in-run-progression.md:146` 的 UI 要求：「支援手把與鍵盤，預設焦點在最左邊選項」。
   手把這一輪不做（沒有手把操作層，只做選單等於選完卡就動不了）。

   鍵盤與滑鼠共用同一個焦點樣式（`.kfocus`）—— 兩套高亮會讓玩家分不清
   「我現在按 Enter 會選到哪一張」。 */

export type ScreenDef = {
  /** DOM id。**必須是既有的 id**（`platform/dom.ts` 的 assign 會抓） */
  id: string;
  /** 可聚焦元素，順序＝導航順序。省略＝掃 `.card` 與 `.btn` */
  focusables?: () => HTMLElement[];
  /** Esc 的行為。不給＝這個畫面不能用 Esc 關（例如升級選卡，非選不可） */
  onEsc?: () => void;
  /** 空白鍵不當「確認」用。**只有對局中彈出來的畫面要開這個**
      （升級選卡、寶箱）：空白鍵在戰鬥中是衝刺，玩家正在連按它的那一刻
      畫面彈出來，就會直接替他選掉一張卡（使用者 2026-08-06 回報）。
      標題頁那類畫面沒有這個問題，空白鍵在那裡是純粹的方便。 */
  noSpace?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
};

var defs: ScreenDef[] = [];
var installed = false;
var focusIdx = 0;

function elOf(d: ScreenDef): any { return $(d.id); }
function isShown(d: ScreenDef) { var e = elOf(d); return !!e && e.classList && e.classList.contains('show'); }

/** 目前開著的畫面（最後註冊的優先 —— 疊在上面的那個接收鍵盤）。 */
export function activeScreen(): ScreenDef | null {
  for (var i = defs.length - 1; i >= 0; i--) if (isShown(defs[i])) return defs[i];
  return null;
}

function listOf(d: ScreenDef): HTMLElement[] {
  if (d.focusables) return d.focusables().filter(Boolean);
  var e = elOf(d); if (!e || !e.querySelectorAll) return [];
  var out: HTMLElement[] = [];
  var ns = e.querySelectorAll('.card,.btn,.focusable');
  for (var i = 0; i < ns.length; i++) {
    var n = ns[i] as HTMLElement;
    /* 停用的按鈕不進導航環：焦點停在一個按了沒反應的東西上，
       讀起來像「介面卡住了」。 */
    if (n.classList.contains('off') || (n as any).disabled) continue;
    out.push(n);
  }
  return out;
}

function paint(d: ScreenDef) {
  var list = listOf(d);
  if (!list.length) return;
  if (focusIdx >= list.length) focusIdx = list.length - 1;
  if (focusIdx < 0) focusIdx = 0;
  for (var i = 0; i < list.length; i++) list[i].classList.toggle('kfocus', i === focusIdx);
}

/** 把焦點設到第 i 個（滑鼠移過去時也會呼叫，兩種輸入共用同一個高亮）。 */
export function setFocus(i: number) {
  var d = activeScreen(); if (!d) return;
  focusIdx = i; paint(d);
}

function move(step: number) {
  var d = activeScreen(); if (!d) return;
  var list = listOf(d); if (!list.length) return;
  focusIdx = (focusIdx + step + list.length) % list.length;
  paint(d);
}

function activate() {
  var d = activeScreen(); if (!d) return;
  var list = listOf(d);
  var el = list[focusIdx];
  if (el) el.click();
}

/** 畫面內容重畫之後叫一次（卡片是每輪重新 innerHTML 的）。 */
export function resetFocus(id?: string) {
  focusIdx = 0;
  var d = id ? defs.filter(function (x) { return x.id === id; })[0] : activeScreen();
  if (d && isShown(d)) paint(d);
}

function onKey(e: KeyboardEvent) {
  var d = activeScreen();
  if (!d) return;
  var k = e.key;
  var kl = k.toLowerCase();
  if (k === 'ArrowRight' || k === 'ArrowDown' || kl === 'd' || kl === 's') { move(1); e.preventDefault(); return; }
  if (k === 'ArrowLeft' || k === 'ArrowUp' || kl === 'a' || kl === 'w') { move(-1); e.preventDefault(); return; }
  if (k === 'Enter' || (k === ' ' && !d.noSpace)) { activate(); e.preventDefault(); return; }
  /* 對局中的畫面：空白鍵**吃掉但不做事**。不 preventDefault 的話它會捲動頁面，
     而且放行下去會被底下的衝刺鍵接到 —— 兩個都不是玩家要的。 */
  if (k === ' ' && d.noSpace) { e.preventDefault(); return; }
  if (k === 'Escape' && d.onEsc) { d.onEsc(); e.preventDefault(); }
}

function sync() {
  var open = !!activeScreen();
  if (typeof document !== 'undefined' && document.body) document.body.classList.toggle('menu-open', open);
}

/** 註冊一個畫面。第一次註冊時順便掛上鍵盤監聽與 `.show` 的觀察器。 */
export function registerScreen(d: ScreenDef) {
  defs.push(d);
  var e = elOf(d);
  if (typeof MutationObserver === 'undefined' || !e || !e.classList) { installKeys(); return; }
  var was = isShown(d);
  new MutationObserver(function () {
    var now = isShown(d);
    if (now === was) return;
    was = now;
    sync();
    if (now) { focusIdx = 0; paint(d); if (d.onOpen) d.onOpen(); }
    else if (d.onClose) d.onClose();
  }).observe(e, { attributes: true, attributeFilter: ['class'] });
  installKeys();
}

function installKeys() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  /* capture 階段：升級畫面開著時，方向鍵不該同時被當成移動輸入吃掉
     （模擬雖然暫停了，但 `sim/input.ts` 仍會記下按鍵狀態，
     關掉畫面的那一瞬間機體會往那個方向衝出去）。 */
  window.addEventListener('keydown', onKey, true);
}
